import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import { inputStyle } from "../OrderEntry/styles";
import { tokens, withAlpha } from '../../Common/designTokens';
import { 
  updateTradeRowCalculations,
  TRADE_CONSTANTS,
  validateCalculatedRow,
} from "../../Utils/tradeCalculationHelpers";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: tokens.colors.bodyBackgroundColor,
  borderRadius: "8px",
  boxShadow: 24,
  p: 3,
};

const headerStyle = {
  pb: 2,
  borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
  mb: 2,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  pt: 3,
  bgcolor: tokens.colors.defaultGridRowColor,
  "& > :not(style) + :not(style)": {
    ml: 2,
  },
};

const EDITABLE_FIELDS = [
  "tradeQuantity",
  "endingQuantity",
  "tradeValue",
  "endingSecurityMarketValue",
];

const FIELD_LABELS = {
  tradeQuantity: "Trade Qty",
  endingQuantity: "Ending Qty",
  tradeValue: "Trade Value",
  endingSecurityMarketValue: "Ending Market Value",
};

const normalizeNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Object.is(num, -0) ? 0 : num;
};

const normalizeEditableValues = (source = {}) => {
  const normalized = {};
  EDITABLE_FIELDS.forEach((field) => {
    normalized[field] = normalizeNumber(source[field]);
  });
  return normalized;
};

const toDisplayValues = (source = {}) => {
  const display = {};
  EDITABLE_FIELDS.forEach((field) => {
    const safeNum = normalizeNumber(source[field]);
    display[field] = safeNum === 0 ? "" : safeNum;
  });
  return display;
};

const getFirstNegativeField = (source = {}) =>
  EDITABLE_FIELDS.find((field) => Number(source[field]) < 0) || null;

const EditQtyPortfolioRebalance = ({
  open,
  onClose,
  rowData,
  originalQty,
  onSubmit,
  isPortfolioPlayMode = false,
}) => {
  // Track which field is being edited
  const [editField, setEditField] = useState("tradeQuantity");
  const [values, setValues] = useState({
    tradeQuantity: 0,
    endingQuantity: 0,
    tradeValue: 0,
    endingSecurityMarketValue: 0,
  });
  const [validationError, setValidationError] = useState("");
  const [displayValues, setDisplayValues] = useState({
    tradeQuantity: "",
    endingQuantity: "",
    tradeValue: "",
    endingSecurityMarketValue: "",
  });
  const [currentSide, setCurrentSide] = useState(rowData?.side);
  const [lastChangedField, setLastChangedField] = useState(null);

  useEffect(() => {
    if (rowData && open) {
      const initialValues = normalizeEditableValues({
        tradeQuantity: rowData.tradeQuantity,
        endingQuantity: rowData.endingQuantity,
        tradeValue: rowData.tradeValue,
        endingSecurityMarketValue: rowData.endingSecurityMarketValue,
      });
      setValues(initialValues);
      setDisplayValues(toDisplayValues(initialValues));
      // Default side to "BUY" when not set (e.g., portfolio play positions)
      const effectiveSide = rowData.side || rowData.action || "BUY";
      setCurrentSide(effectiveSide);
      setEditField("tradeQuantity");
      setLastChangedField(null);

      const firstNegativeField = getFirstNegativeField(initialValues);
      setValidationError(
        firstNegativeField
          ? `${FIELD_LABELS[firstNegativeField]} cannot be negative. Update it to 0 or higher to enable Submit.`
          : ""
      );
    }
  }, [rowData, open]);

  if (!rowData) return null;

  const currentPrice = rowData.currentPrice || 0;
  const currentQty = rowData.currentQuantity || 0;
  const accruedInterest = rowData.accruedInterest || 0;
  
  // Calculate current market value
  const currentMarketValue = currentQty * ((currentPrice + accruedInterest) / 100);

  // Handle field focus to set active field
  const handleFieldFocus = (field) => {
    if (field !== editField) {
      setEditField(field);
      setValidationError("");
    }
  };

  // Handle value input for the active field
  const handleValueChange = (field, newValue) => {
    // Treat undefined/null/empty as 0 and allow temporarily negative values for correction.
    if (newValue === undefined || newValue === null || newValue === "" || (typeof newValue === "number" && !isNaN(newValue))) {
      const numValue = normalizeNumber(newValue);
      setValidationError("");
      setEditField(field);
      setLastChangedField(field);

      const updatedValues = { ...values, [field]: numValue };
      setValues(updatedValues);
      setDisplayValues((prev) => ({
        ...prev,
        [field]: numValue === 0 ? "" : numValue,
      }));

      const calculatedRow = applyCalculations(field, numValue, currentSide, updatedValues);
      if (!calculatedRow) return;

      const normalizedCalculatedValues = normalizeEditableValues(calculatedRow);
      setValues(normalizedCalculatedValues);

      // Display 0 as empty string, otherwise show the calculated value
      setDisplayValues(toDisplayValues(normalizedCalculatedValues));
      setCurrentSide(calculatedRow.side || currentSide);
    }
  };

  const firstNegativeField = getFirstNegativeField(values);
  const hasInvalidNumbers = Object.values(values).some(
    (value) => typeof value !== "number" || isNaN(value)
  );
  const disableReason = validationError ||
    (firstNegativeField
      ? `${FIELD_LABELS[firstNegativeField]} cannot be negative. Set it to 0 or higher to submit.`
      : hasInvalidNumbers
      ? "Please enter valid numeric values."
      : "");
  const isSubmitDisabled = Boolean(disableReason);

  const isFieldNegative = (field) => Number(values[field]) < 0;

  // Validate and submit
  const handleSubmit = () => {
    if (isSubmitDisabled) {
      if (!validationError && disableReason) {
        setValidationError(disableReason);
      }
      return;
    }

    const submitField = lastChangedField || editField;
    const activeValue = values[submitField];

    if (activeValue === "" || isNaN(Number(activeValue)) || Number(activeValue) < 0) {
      setValidationError("Please enter a valid value");
      return;
    }

    const calculatedRow = applyCalculations(submitField, Number(activeValue), currentSide);
    if (!calculatedRow) return;

    onSubmit({
      tradeQuantity: calculatedRow.tradeQuantity,
      endingQuantity: calculatedRow.endingQuantity,
      tradeValue: calculatedRow.tradeValue,
      endingSecurityMarketValue: calculatedRow.endingSecurityMarketValue,
      side: calculatedRow.side,
    });
    onClose();
  };

  // Common input styles for grid cells
  const gridInputStyle = {
    ...inputStyle,
    "& .MuiInputBase-input::placeholder": {
      color: tokens.colors.fontColour85,
      opacity: 1,
    },
    "& .MuiFilledInput-input:-webkit-autofill": {
      WebkitBoxShadow: `0 0 0 1000px ${tokens.colors.white} inset`,
      WebkitTextFillColor: tokens.colors.fontColour,
    },
    // "& .MuiFilledInput-input:disabled": {
    //   backgroundColor: tokens.colors.bodyBackgroundColor,
    //   color: tokens.colors.fontColour85,
    //   WebkitTextFillColor: tokens.colors.fontColour85,
    // },
  };

  // Validate numeric input - prevent entry of values exceeding limits
  const isAllowedQty = (values) => {
    const { floatValue } = values;
    if (floatValue === undefined) return true; // Allow empty
    return floatValue <= TRADE_CONSTANTS.MAX_QUANTITY;
  };

  const isAllowedValue = (values) => {
    const { floatValue } = values;
    if (floatValue === undefined) return true; // Allow empty
    return floatValue <= TRADE_CONSTANTS.MAX_VALUE;
  };

  const applyCalculations = (field, numericValue, sideOverride = currentSide, baseValues = values) => {
    const calculatedRow = updateTradeRowCalculations(
      {
        ...rowData,
        ...baseValues,
        [field]: numericValue,
        side: sideOverride,
        action: sideOverride,
      },
      field,
      numericValue
    );

    const calcValidation = validateCalculatedRow(calculatedRow);
    if (!calcValidation.isValid) {
      setValidationError(calcValidation.error);
      return null;
    }

    // sell-side validation against current holdings
    const finalSide = calculatedRow.side;
    const finalTradeQty = Number(calculatedRow.tradeQuantity ?? 0);
    if ((finalSide === "SELL" || finalSide === "Sell") && finalTradeQty > currentQty) {
      setValidationError(
        `Cannot sell more than available quantity. Available: ${currentQty.toLocaleString()}, Required: ${finalTradeQty.toLocaleString()}.`
      );
      return null;
    }

    return calculatedRow;
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-qty-modal-title">
      <Box
        sx={{
          ...modalStyle,
          bgcolor: tokens.colors.bodyBackgroundColor,
        }}
      >
        <Box sx={headerStyle}>
          <Typography id="edit-qty-modal-title" variant="h6" fontWeight="bold" sx={{ color: tokens.colors.fontColour }}>
            Edit Trade Fields
          </Typography>
        </Box>

        {/* Summary info */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            bgcolor: tokens.colors.defaultGridRowColor,
            my: 2,
            borderRadius: "4px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              CUSIP: {rowData.cusip}
            </Typography>
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: tokens.colors.bodyBorderColor50,
                height: 20,
              }}
            />
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              Side: <span style={{ color: currentSide === "BUY" || currentSide === "Buy" ? tokens.colors.buyFontColor : tokens.colors.sellFontColor }}>
                {currentSide}
              </span>
            </Typography>
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: tokens.colors.bodyBorderColor50,
                height: 20,
              }}
            />
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              Price: {(currentPrice || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
            </Typography>
            <Divider
              orientation="vertical"
              sx={{
                backgroundColor: tokens.colors.bodyBorderColor50,
                height: 20,
              }}
            />
            <Typography sx={{ fontWeight: "bold", color: tokens.colors.fontColour }}>
              Acc Interest: {(accruedInterest || 0).toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
            </Typography>
          </Box>
        </Box>
        {/* //horizontal divider */}
        <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50, mb: 3 }} />

        {/* Grid Layout for Input Fields */}
        <Box sx={{ px: 1, pb: 2 }}>
          {/* Column Headers */}
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "80px 1fr 1fr 1fr", 
            gap: 2, 
            mb: 1,
            alignItems: "center",
          }}>
            <Box /> {/* Empty cell for row label column */}
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour, textAlign: "center" }}>
              Current
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour, textAlign: "center" }}>
              Trade
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour, textAlign: "center" }}>
              Ending
            </Typography>
          </Box>

          {/* Horizontal divider below headers */}
          <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50, mb: 2 }} />

          {/* Qty Row */}
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "80px 1fr 1fr 1fr", 
            gap: 2, 
            mb: 2,
            alignItems: "center",
          }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour }}>
              Qty
            </Typography>

            {/* Current Qty (Read-only) */}
            <NumericFormat
              customInput={TextField}
              fullWidth
              value={currentQty}
              variant="filled"
              placeholder="0"
              thousandSeparator=","
              allowNegative={false}
              decimalScale={0}
              disabled={true}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
            />
            
            {/* Trade Qty */}
            <NumericFormat
              customInput={TextField}
              fullWidth
              value={displayValues.tradeQuantity}
              variant="filled"
              placeholder="0"
              thousandSeparator=","
              allowNegative={true}
              decimalScale={0}
              disabled={false}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
              error={isFieldNegative("tradeQuantity")}
              isAllowed={isAllowedQty}
              onFocus={() => handleFieldFocus("tradeQuantity")}
              onValueChange={(v, sourceInfo) => {
                if (sourceInfo.source === "event") {
                  handleValueChange("tradeQuantity", v.floatValue === undefined || v.floatValue === null ? undefined : v.floatValue);
                }
              }}
            />
            
            {/* Ending Qty */}
            <NumericFormat
              customInput={TextField}
              fullWidth
              value={displayValues.endingQuantity}
              variant="filled"
              placeholder="0"
              thousandSeparator=","
              allowNegative={true}
              decimalScale={0}
              disabled={false}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
              error={isFieldNegative("endingQuantity")}
              isAllowed={isAllowedQty}
              onFocus={() => handleFieldFocus("endingQuantity")}
              onValueChange={(v, sourceInfo) => {
                if (sourceInfo.source === "event") {
                  handleValueChange("endingQuantity", v.floatValue === undefined || v.floatValue === null ? undefined : v.floatValue);
                }
              }}
            />
          </Box>

          {/* Horizontal divider between rows */}
          <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50, mb: 2 }} />

          {/* Value Row */}
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: "80px 1fr 1fr 1fr", 
            gap: 2, 
            mb: 2,
            alignItems: "center",
          }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: tokens.colors.fontColour }}>
              Value
            </Typography>

            {/* Current Market Value (Read-only) */}
            <NumericFormat
              customInput={TextField}
              fullWidth
              value={currentMarketValue}
              variant="filled"
              placeholder="0.00"
              thousandSeparator=","
              allowNegative={false}
              decimalScale={2}
              disabled={true}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
            />
            
            {/* Trade Value */}
            <NumericFormat
              customInput={TextField}
              fullWidth
              value={displayValues.tradeValue}
              variant="filled"
              placeholder="0.00"
              thousandSeparator=","
              allowNegative={true}
              decimalScale={2}
              disabled={false}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
              error={isFieldNegative("tradeValue")}
              isAllowed={isAllowedValue}
              onFocus={() => handleFieldFocus("tradeValue")}
              onValueChange={(v, sourceInfo) => {
                if (sourceInfo.source === "event") {
                  handleValueChange("tradeValue", v.floatValue === undefined || v.floatValue === null ? undefined : v.floatValue);
                }
              }}
            />
            
            {/* Ending Market Value */}
            <NumericFormat
              customInput={TextField}
              fullWidth
              value={displayValues.endingSecurityMarketValue}
              variant="filled"
              placeholder="0.00"
              thousandSeparator=","
              allowNegative={true}
              decimalScale={2}
              disabled={false}
              InputProps={{ disableUnderline: true }}
              sx={gridInputStyle}
              error={isFieldNegative("endingSecurityMarketValue")}
              isAllowed={isAllowedValue}
              onFocus={() => handleFieldFocus("endingSecurityMarketValue")}
              onValueChange={(v, sourceInfo) => {
                if (sourceInfo.source === "event") {
                  handleValueChange("endingSecurityMarketValue", v.floatValue === undefined || v.floatValue === null ? undefined : v.floatValue);
                }
              }}
            />
          </Box>

          {/* Show validation error */}
          {disableReason && (
            <Alert 
              severity="error" 
              sx={{ 
                backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                color: tokens.palette.danger,
                border: `1px solid ${tokens.palette.danger}`,
                "& .MuiAlert-icon": {
                  color: tokens.palette.danger,
                },
              }}
            >
              {disableReason}
            </Alert>
          )}
        </Box>

        <Box sx={{ mt: 3, mx: -3 }}>
          <Divider sx={{ borderColor: tokens.colors.bodyBorderColor50 }} />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            ...footerStyle,
            borderTop: "none",
            bgcolor: tokens.colors.bodyBackgroundColor,
            "& > :not(style) + :not(style)": {
              ml: 2,
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              backgroundColor: tokens.palette.primary,
              "&:hover": {
                backgroundColor: tokens.palette.primaryA80,
              },
              opacity: isSubmitDisabled ? 0.5 : 1,
              cursor: isSubmitDisabled ? "not-allowed" : "pointer",
              borderRadius: "8px",
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditQtyPortfolioRebalance;